import { useEffect } from "react";
import logger from "../utils/logger";

/**
 * Custom hook to manage action timeout checking
 * 
 * Monitors action history for pending actions that have exceeded the 60-second
 * timeout threshold. When a timeout is detected:
 * - Marks the action as timed out
 * - For DELETE operations, marks device-credential pair as offline
 * 
 * @param {Function} setActionHistory - Function to update action history
 * @param {Function} setOfflineDeviceCredentials - Function to update offline device credentials
 * @param {Function} makePendingKey - Function to create device-credential keys
 * @param {Object} selectedDeviceIdRef - Ref containing the currently selected device ID
 */
export const useActionTimeouts = (
  setActionHistory,
  setOfflineDeviceCredentials,
  makePendingKey,
  selectedDeviceIdRef
) => {
  useEffect(() => {
    const timeoutChecker = setInterval(() => {
      const now = Date.now();
      const currentDevice = selectedDeviceIdRef.current;

      // Filter actions for the current device only
      // (Removed unused currentDeviceActions variable - it's filtered but not used)

      // Logging disabled to reduce console spam
      // logger.log(
      //   `[Timeout Checker] 📋 Checking ${actionHistory.length} total actions, ${currentDeviceActions.length} for current device (${currentDevice})`
      // );
      // currentDeviceActions.forEach((action, index) => {
      //   if (
      //     action.actionStatus === "PENDING" ||
      //     action.actionStatus === "60-SEC TIMEOUT"
      //   ) {
      //     const age = now - new Date(action.createdAt).getTime();
      //     logger.log(
      //       `[Timeout Checker] - Action ${index}: ${action.actionId?.substring(
      //         0,
      //         8
      //       )}..., status: ${action.actionStatus}, operation: ${
      //         action.operation || action.actionType
      //       }, deviceId: ${action.deviceId}, age: ${Math.round(
      //         age / 1000
      //       )}s, isTimedOut: ${action.isTimedOut}`
      //     );
      //   }
      // });

      setActionHistory((currentHistory) => {
        let wasChanged = false;
        const updatedHistory = currentHistory.map((action) => {
          // Only process actions for the current device
          if (action.deviceId !== currentDevice) {
            return action;
          }

          const hasTimedOut =
            action.actionStatus === "60-SEC TIMEOUT" ||
            (action.actionStatus === "PENDING" &&
              !action.isTimedOut &&
              now - new Date(action.createdAt).getTime() > 60000);

          if (hasTimedOut && !action.isTimedOut) {
            wasChanged = true;

            logger.log(`[Timeout Checker] ⏰ Action timed out:`, {
              actionId: action.actionId,
              actionStatus: action.actionStatus,
              operation: action.operation,
              actionType: action.actionType,
              deviceId: action.deviceId,
              credentialId: action.credentialId,
            });

            // If this is a DELETE operation, mark the device-credential pair as offline
            const isDeleteOperation =
              action.operation === "DELETE" ||
              action.actionType === "DELETE_CREDENTIAL";
            if (isDeleteOperation && action.credentialId && action.deviceId) {
              const offlineKey = makePendingKey(
                action.deviceId,
                action.credentialId
              );
              setOfflineDeviceCredentials((prev) => {
                const updated = new Set(prev).add(offlineKey);
                logger.log(
                  `[Timeout Checker] 🔴 Device offline detected for ${offlineKey}`,
                  `(action status: ${action.actionStatus})`
                );
                return updated;
              });
            } else if (isDeleteOperation && !action.deviceId) {
              logger.error(
                `[Timeout Checker] ❌ Cannot mark offline: action missing deviceId`,
                action
              );
            }

            return { ...action, isTimedOut: true };
          }
          return action;
        });
        return wasChanged ? updatedHistory : currentHistory;
      });
    }, 1000); // Check every second for timeouts

    return () => clearInterval(timeoutChecker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies - uses functional updates and refs (setters are stable, makePendingKey is in closure)
};

export default useActionTimeouts;
